/**
 * Shared ADPOLY faculty bulk-upload helpers (Admins + Academics).
 * Entity / campus / faculty_type come from the route, not the Excel file.
 */

export const FACULTY_BULK_TEMPLATE_FILENAME =
    "adpoly-faculty-bulk-upload-template.xlsx";

/** Bundled template URL from src/assets/templates */
export const FACULTY_BULK_TEMPLATE_URL = require("../../assets/templates/adpoly-faculty-bulk-upload-template.xlsx");

/** Headers expected in the Excel template (Faculty sheet). */
export const FACULTY_BULK_HEADERS = [
    "STAFF ID",
    "NAME",
    "EMAIL",
    "JOB TITLE",
    "DEPARTMENT",
    "DO YOU WANT TO CREATE AN ACCOUNT FOR THIS PERSON?",
];

export const FACULTY_BULK_REQUIRED_HEADERS = [
    "NAME",
    "EMAIL",
    "JOB TITLE",
    "DEPARTMENT",
];

export const FACULTY_BULK_DEFAULT_PASSWORD = "123456";
export const FACULTY_BULK_DEFAULT_ROLE = "faculty";

var CREATE_ACCOUNT_HEADER = "DO YOU WANT TO CREATE AN ACCOUNT FOR THIS PERSON?";

var HEADER_ALIASES = {
    "STAFF ID": ["STAFF ID", "STAFFID", "STAFF_ID", "EMPLOYEE NUMBER", "EMPLOYEE NO"],
    NAME: ["NAME", "FULL NAME", "EMPLOYEE NAME"],
    EMAIL: ["EMAIL", "EMAIL ADDRESS", "E-MAIL", "E MAIL"],
    "JOB TITLE": ["JOB TITLE", "JOBTITLE", "JOB_TITLE", "POSITION", "TITLE"],
    DEPARTMENT: ["DEPARTMENT", "PROGRAM", "DEPT"],
};
HEADER_ALIASES[CREATE_ACCOUNT_HEADER] = [
    CREATE_ACCOUNT_HEADER,
    "DO YOU WANT TO CREATE AN ACCOUNT FOR THIS PERSON",
    "CREATE ACCOUNT",
    "CREATE_ACCOUNT",
    "CREATE USER ACCOUNT",
    "CREATE LOGIN",
];

export function normalizeBulkHeader(value) {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/\u00a0/g, " ")
        .trim()
        .replace(/[_]+/g, " ")
        .replace(/\s+/g, " ")
        .toUpperCase();
}

export function mapBulkHeaders(rawHeaders) {
    var normalized = (rawHeaders || []).map(normalizeBulkHeader);
    var mapping = {};
    var found = {};

    Object.keys(HEADER_ALIASES).forEach(function (canonical) {
        var aliases = HEADER_ALIASES[canonical];
        for (var i = 0; i < normalized.length; i++) {
            if (aliases.indexOf(normalized[i]) !== -1) {
                mapping[canonical] = i;
                found[canonical] = true;
                break;
            }
        }
    });

    var missing = FACULTY_BULK_HEADERS.filter(function (header) {
        return !found[header];
    });

    return {
        mapping: mapping,
        missing: missing,
        normalized: normalized,
    };
}

export function cellToString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "number" && !isNaN(value)) {
        // Avoid scientific notation for staff IDs that Excel may store as numbers
        if (Math.floor(value) === value) {
            return String(Math.trunc(value));
        }
        return String(value);
    }
    return String(value).replace(/\u00a0/g, " ").trim();
}

/**
 * Parse CREATE ACCOUNT cell. Blank => false.
 * @returns {{ ok: boolean, value?: boolean, error?: string }}
 */
export function parseCreateAccountValue(raw) {
    var text = cellToString(raw);
    if (text === "") {
        return { ok: true, value: false };
    }

    var normalized = text.toLowerCase();
    var yesValues = ["yes", "y", "true", "1"];
    var noValues = ["no", "n", "false", "0"];

    if (yesValues.indexOf(normalized) !== -1) {
        return { ok: true, value: true };
    }
    if (noValues.indexOf(normalized) !== -1) {
        return { ok: true, value: false };
    }

    return {
        ok: false,
        error:
            'Create account column must be Yes or No (accepted: Yes/No, Y/N, True/False, 1/0). Found "' +
            text +
            '".',
    };
}

export function isValidEmail(email) {
    if (!email) return false;
    // Practical email check aligned with common frontend validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Build validated rows from sheet_to_json objects (array of arrays preferred).
 * @param {Array<Array>} aoa - sheet as array-of-arrays including header row
 */
export function parseAndValidateFacultyBulkRows(aoa) {
    var errors = [];
    var rows = [];

    if (!aoa || !aoa.length) {
        return {
            ok: false,
            errors: [
                {
                    row: 0,
                    message: "The Excel file is empty.",
                },
            ],
            rows: [],
        };
    }

    var headerRow = aoa[0] || [];
    var headerInfo = mapBulkHeaders(headerRow);

    if (headerInfo.missing.length) {
        return {
            ok: false,
            errors: [
                {
                    row: 1,
                    message:
                        "Missing required column(s): " +
                        headerInfo.missing.join(", ") +
                        ". Please use the official template.",
                },
            ],
            rows: [],
        };
    }

    var map = headerInfo.mapping;
    var emailsSeen = {};

    for (var i = 1; i < aoa.length; i++) {
        var excelRow = i + 1;
        var line = aoa[i] || [];

        var staffId = cellToString(line[map["STAFF ID"]]);
        var name = cellToString(line[map.NAME]);
        var email = cellToString(line[map.EMAIL]).toLowerCase();
        var jobTitle = cellToString(line[map["JOB TITLE"]]);
        var department = cellToString(line[map.DEPARTMENT]);
        var createAccountRaw = line[map[CREATE_ACCOUNT_HEADER]];

        var isBlankRow =
            !staffId && !name && !email && !jobTitle && !department && !cellToString(createAccountRaw);

        if (isBlankRow) {
            continue;
        }

        var rowErrors = [];

        if (!name) {
            rowErrors.push("NAME is required.");
        }
        if (!email) {
            rowErrors.push("EMAIL is required.");
        } else if (!isValidEmail(email)) {
            rowErrors.push('EMAIL "' + email + '" is not a valid email address.');
        } else if (emailsSeen[email]) {
            rowErrors.push(
                'EMAIL "' + email + '" is duplicated in the file (also on row ' + emailsSeen[email] + ")."
            );
        }
        if (!jobTitle) {
            rowErrors.push("JOB TITLE is required.");
        }
        if (!department) {
            rowErrors.push("DEPARTMENT is required.");
        }

        var createAccountParsed = parseCreateAccountValue(createAccountRaw);
        if (!createAccountParsed.ok) {
            rowErrors.push(createAccountParsed.error);
        }

        if (rowErrors.length) {
            for (var e = 0; e < rowErrors.length; e++) {
                errors.push({
                    row: excelRow,
                    message: rowErrors[e],
                });
            }
            continue;
        }

        emailsSeen[email] = excelRow;

        rows.push({
            excelRow: excelRow,
            staff_id: staffId || null,
            name: name,
            email: email,
            job_title: jobTitle,
            department: department,
            create_account: !!createAccountParsed.value,
        });
    }

    if (!rows.length && !errors.length) {
        errors.push({
            row: 0,
            message: "No data rows found. Add at least one person and remove empty sample rows if needed.",
        });
    }

    return {
        ok: errors.length === 0 && rows.length > 0,
        errors: errors,
        rows: rows,
    };
}
